export type ToastKind = "error" | "success" | "info";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastItem {
  id: string;
  kind: ToastKind;
  message: string;
  /** Optional inline action, e.g. "Ongedaan maken". Clicking it runs onClick and dismisses the toast. */
  action?: ToastAction;
  durationMs: number;
  createdAt: number;
}

type Listener = () => void;

const DEFAULT_DURATION_MS = 5000;
// Undo toasts stay a little longer so there's time to reach the button.
const ACTION_DURATION_MS = 7000;

let toasts: ToastItem[] = [];
let nextId = 0;
const listeners = new Set<Listener>();

function emit() {
  listeners.forEach((listener) => listener());
}

/** Shows a toast and returns its id. */
export function toast(message: string, opts?: { kind?: ToastKind; action?: ToastAction; durationMs?: number }): string {
  const id = String(++nextId);
  const durationMs = opts?.durationMs ?? (opts?.action ? ACTION_DURATION_MS : DEFAULT_DURATION_MS);
  toasts = [...toasts, { id, kind: opts?.kind ?? "info", message, action: opts?.action, durationMs, createdAt: Date.now() }];
  emit();
  return id;
}

/** Shows an error toast, unless one with the same message is already showing — in which case this is a no-op, so a request's own retries (e.g. React Query's default 3x query retry) don't re-trigger or extend an already-visible toast for the same failure. */
export function notifyError(message: string): void {
  if (toasts.some((t) => t.kind === "error" && t.message === message)) return;
  toast(message, { kind: "error" });
}

export function dismissToast(id: string): void {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

export function subscribeToasts(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getToastSnapshot(): ToastItem[] {
  return toasts;
}
